import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { formatApiError } from "./AuthContext";
import { brand } from "@/content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [state, setState] = useState({ saving: false, error: "", done: false });

  const submit = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      setState({ saving: false, error: "Passwords do not match.", done: false });
      return;
    }
    setState({ saving: true, error: "", done: false });
    try {
      await axios.post(`${API}/auth/set-password`, { token, newPassword: pw.next });
      setState({ saving: false, error: "", done: true });
      setTimeout(() => { window.location.href = "/admin"; }, 1600);
    } catch (err) {
      setState({ saving: false, error: formatApiError(err), done: false });
    }
  };

  return (
    <div className="min-h-screen bg-soft flex items-center justify-center p-5">
      <div className="w-full max-w-md rounded-2xl bg-white border border-border-neutral p-8" data-testid="set-password-page">
        <div className="flex items-center gap-3 mb-6">
          <img src={brand.logo} alt="Green Circle Group" className="h-10 w-auto" />
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
        </div>

        {!token ? (
          <div data-testid="set-password-invalid">
            <h1 className="text-2xl font-extrabold text-forest">Invalid link</h1>
            <p className="mt-2 text-sm text-mid-neutral">
              This password link is missing its token. Ask your admin to send a new invite.
            </p>
          </div>
        ) : state.done ? (
          <div data-testid="set-password-success">
            <CheckCircle2 className="h-10 w-10 text-mint" />
            <h1 className="mt-3 text-2xl font-extrabold text-forest">You're all set</h1>
            <p className="mt-2 text-sm text-mid-neutral">
              Password saved. Taking you to the CRM…
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold text-forest flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-mint" /> Set your password
            </h1>
            <p className="mt-2 text-sm text-mid-neutral">
              Choose a password (8+ characters) to activate your Green Circle CRM account.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input
                type="password"
                required
                minLength={8}
                placeholder="New password"
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
                data-testid="set-password-new"
                className="w-full p-3 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest"
              />
              <input
                type="password"
                required
                minLength={8}
                placeholder="Confirm password"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                data-testid="set-password-confirm"
                className="w-full p-3 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest"
              />
              {state.error && (
                <p data-testid="set-password-error" className="text-sm text-red-700">{state.error}</p>
              )}
              <button
                type="submit"
                disabled={state.saving || pw.next.length < 8}
                data-testid="set-password-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-forest text-white px-4 py-3 text-sm font-bold disabled:opacity-50"
              >
                {state.saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Set password & sign in
              </button>
            </form>
          </>
        )}

        <p className="mt-6 text-center text-xs text-mid-neutral">
          <Link to="/admin/login" className="font-semibold text-forest hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
